"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSelector, useDispatch } from 'react-redux';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, MessageSquare, Users, Shield, Megaphone, Info } from 'lucide-react';
import { MdNotifications } from 'react-icons/md';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../../lib/utils';

// Notification type config with icons and colors
const NOTIFICATION_TYPE_CONFIG = {
  group_message: { icon: MessageSquare, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-950', label: 'Group Message' },
  group_mention: { icon: MessageSquare, color: 'text-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-950', label: 'Mention' },
  member_added: { icon: Users, color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-950', label: 'Added to Group' },
  member_removed: { icon: Users, color: 'text-red-500', bgColor: 'bg-red-50 dark:bg-red-950', label: 'Removed' },
  admin_promoted: { icon: Shield, color: 'text-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-950', label: 'Admin' },
  announcement: { icon: Megaphone, color: 'text-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-950', label: 'Announcement' },
  system: { icon: Info, color: 'text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-950', label: 'System' },
  general: { icon: Bell, color: 'text-primary', bgColor: 'bg-primary/5', label: 'General' },
};

/**
 * Notification Bell with Dropdown
 * Shows in the dashboard header
 */
const NotificationBell = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const { unreadCount, markAsRead, markAllAsRead, loadNotifications } = useNotifications();
  const notifications = useSelector((state) => state.notifications.notifications);

  // Load recent notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications({ page: 1, limit: 8 });
    }
  }, [isOpen]); // eslint-disable-line

  // Use redux notifications as recent
  useEffect(() => {
    setRecentNotifications(notifications.slice(0, 8));
  }, [notifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle notification click
  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }
    setIsOpen(false);
    if (notification.clickAction) {
      router.push(notification.clickAction);
    }
  }, [markAsRead, router]);

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  // View all
  const handleViewAll = () => {
    setIsOpen(false);
    router.push('/dashboard/notifications');
  };

  const formatTime = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        className="relative h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 p-0 hover:bg-transparent"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MdNotifications className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
                  {unreadCount}
                </Badge>
              )}
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleMarkAllRead}
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Bell className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              recentNotifications.map((notification) => {
                const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type] || NOTIFICATION_TYPE_CONFIG.general;
                const IconComponent = typeConfig.icon;

                return (
                  <button
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-border/50 last:border-0",
                      "hover:bg-muted/50",
                      !notification.isRead && "bg-primary/5"
                    )}
                  >
                    {/* Icon */}
                    <div className={cn(
                      "flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mt-0.5",
                      typeConfig.bgColor
                    )}>
                      <IconComponent className={cn("h-4 w-4", typeConfig.color)} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm truncate",
                        !notification.isRead ? "font-semibold text-foreground" : "font-medium text-muted-foreground"
                      )}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.body}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {formatTime(notification.createdAt)}
                        </span>
                        <Badge variant="outline" className="h-4 px-1 text-[9px]">
                          {typeConfig.label}
                        </Badge>
                      </div>
                    </div>

                    {/* Unread dot */}
                    {!notification.isRead && (
                      <div className="flex-shrink-0 h-2 w-2 rounded-full bg-primary mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-2 bg-muted/30">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs text-primary hover:text-primary/80"
              onClick={handleViewAll}
            >
              View all notifications
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
